import { Users } from '@platform/client';
import { UpdateProfileRequest } from '@platform/client/schemas';
import { useEffect, useState } from 'react';
import { Route, Switch, useLocation } from 'wouter';
import { Step } from '../application/Application';
import { useApplicationLocalStorage } from '../application/application-hook';
import { ExperienceDetailSelection } from '../application/profile/shared/ExperienceDetailSelection';
import { LocationSelection } from '../application/profile/shared/LocationSelection';
import { ProfilePictureUpload } from '../application/profile/shared/ProfilePictureUpload';
import { ResumeSelection } from '../application/profile/shared/ResumeSelection';
import { SkillsExperience } from '../application/skills/SkillsSelection';

enum EditProfileStep {
  LOCATION_SELECTION = 'LOCATION_SELECTION',
  RESUME_SELECTION = 'RESUME_SELECTION',
  EXPERIENCE_DETAIL_SELECTION = 'EXPERIENCE_DETAIL_SELECTION',
  PHOTO_SELECTION = 'PHOTO_SELECTION',
  SKILLS_SELECTION = 'SKILLS_SELECTION',
}

const EditProfileSteps: Record<EditProfileStep, Step> = {
  [EditProfileStep.LOCATION_SELECTION]: {
    progress: 20,
    component: LocationSelection,
    url: '/',
  },
  [EditProfileStep.RESUME_SELECTION]: {
    progress: 20,
    component: ResumeSelection,
    url: '/resume-selection',
  },
  [EditProfileStep.EXPERIENCE_DETAIL_SELECTION]: {
    progress: 20,
    component: ExperienceDetailSelection,
    url: '/experience-selection',
  },
  [EditProfileStep.PHOTO_SELECTION]: {
    progress: 20,
    component: ProfilePictureUpload,
    url: '/photo-selection',
  },
  [EditProfileStep.SKILLS_SELECTION]: {
    progress: 160,
    component: SkillsExperience,
    url: '/skills-selection',
  },
} as const;

export const EditProfileRootPro = () => {
  const [location, navigate] = useLocation();
  const [intiialized, setInitialized] = useState(false);

  const { getApplicationData, updateApplicationData } =
    useApplicationLocalStorage<UpdateProfileRequest>();

  const { data: initialData } = Users.useGetProfile();

  useEffect(() => {
    if (initialData) {
      updateApplicationData({
        jobTitle: initialData.jobTitle,
        linkedin: initialData.linkedin,
        resume: initialData.resume,
        skills: initialData.skills,
        country: initialData.country,
        city: initialData.city,
        timezone: initialData.timezone,
        yearsOfExperience: initialData.yearsOfExperience,
        industries: initialData.industries,
        languages: initialData.languages,
        profilePicture: initialData.profilePicture,
      });
      setInitialized(true);
    }
  }, [initialData, updateApplicationData]);

  const submitProfileUpdate = Users.useUpdateProfile();

  const goToNextStep = () => {
    const steps = Object.values(EditProfileStep);
    const currentIndex = steps.findIndex((step) => {
      return EditProfileSteps[step as EditProfileStep].url === location;
    });
    const nextStep =
      currentIndex < steps.length - 1 ? steps[currentIndex + 1] : steps[currentIndex];
    const nextUrl = EditProfileSteps[nextStep as EditProfileStep].url;
    navigate(nextUrl);
    return nextStep as EditProfileStep;
  };

  const goToPreviousStep = () => {
    const steps = Object.values(EditProfileStep);
    const currentIndex = steps.findIndex(
      (step) => EditProfileSteps[step as EditProfileStep].url === location,
    );
    const previousStep = currentIndex > 0 ? steps[currentIndex - 1] : steps[currentIndex]; // should this be dashboard?
    const previousUrl = EditProfileSteps[previousStep as EditProfileStep].url;
    navigate(previousUrl);
    return previousStep as EditProfileStep;
  };

  const onSubmit = () => {
    const profileData = getApplicationData();
    submitProfileUpdate.mutate(
      {
        data: { ...profileData },
      },
      {
        onSuccess: () => {
          window.location.href = 'platform/dashboard';
        },
      },
    );
  };

  if (!intiialized) {
    return null;
  }

  return (
    <Switch>
      {Object.entries(EditProfileSteps).map(([_key, value]) => {
        const { url, component: Component, progress } = value;
        return (
          <Route key={url} path={url}>
            <Component
              onBack={goToPreviousStep}
              onContinue={goToNextStep}
              progress={progress}
              isEditProfile={true}
              onSubmit={onSubmit}
            />
          </Route>
        );
      })}
    </Switch>
  );
};
